<template>
  <div class="space-y-4">
    <div>
      <label class="block text-sm font-medium text-discord-light-100 mb-3">
        {{ t('createWorkspace.visual.label') }}
      </label>
      
      <!-- Acordeão para escolher tipo -->
      <div class="space-y-2">
        <!-- Opção Ícone -->
        <div class="border border-discord-dark-200 rounded overflow-hidden transition-all duration-200 hover:border-discord-dark-100" :class="type === 'icon' ? 'border-discord-blurple shadow-lg shadow-discord-blurple/20' : ''">
          <button
            @click="setType('icon')"
            type="button"
            class="w-full p-5 text-left flex items-center justify-between transition-all duration-200"
            :class="type === 'icon' ? 'bg-gradient-to-r from-discord-blurple/10 to-discord-blurple/5' : 'hover:bg-discord-dark-200/50'"
          >
            <div class="flex items-center">
              <div class="w-10 h-10 rounded flex items-center justify-center mr-4 transition-all duration-200" :class="type === 'icon' ? 'bg-discord-blurple shadow-lg' : 'bg-discord-dark-200'">
                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/>
                </svg>
              </div>
              <div>
                <p class="font-semibold text-discord-light-100 text-base">{{ t('createWorkspace.visual.iconTitle') }}</p>
                <p class="text-sm text-discord-light-500">{{ t('createWorkspace.visual.iconDescription') }}</p>
              </div>
            </div>
            <div class="flex items-center">
              <div v-if="type === 'icon'" class="w-2 h-2 bg-discord-blurple rounded-full mr-3"></div>
              <svg 
                class="w-5 h-5 text-discord-light-400 transition-all duration-200"
                :class="type === 'icon' ? 'rotate-180 text-discord-blurple' : ''"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </div>
          </button>
          
          <!-- Conteúdo do ícone -->
          <div v-if="type === 'icon'" class="px-5 pb-5 bg-gradient-to-b from-discord-blurple/5 to-transparent">
            <div class="border-t border-discord-dark-200 pt-4">
              <IconSelector v-model="selectedIcon" :disabled="disabled" />
            </div>
          </div>
        </div>

        <!-- Opção Upload -->
        <div class="border border-discord-dark-200 rounded overflow-hidden transition-all duration-200 hover:border-discord-dark-100" :class="type === 'upload' ? 'border-discord-green shadow-lg shadow-discord-green/20' : ''">
          <button
            @click="setType('upload')"
            type="button"
            class="w-full p-5 text-left flex items-center justify-between transition-all duration-200"
            :class="type === 'upload' ? 'bg-gradient-to-r from-discord-green/10 to-discord-green/5' : 'hover:bg-discord-dark-200/50'"
          >
            <div class="flex items-center">
              <div class="w-10 h-10 rounded flex items-center justify-center mr-4 transition-all duration-200" :class="type === 'upload' ? 'bg-discord-green shadow-lg' : 'bg-discord-dark-200'">
                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
              </div>
              <div>
                <p class="font-semibold text-discord-light-100 text-base">{{ t('createWorkspace.visual.uploadTitle') }}</p>
                <p class="text-sm text-discord-light-500">{{ t('createWorkspace.visual.uploadDescription') }}</p>
              </div>
            </div>
            <div class="flex items-center">
              <div v-if="type === 'upload'" class="w-2 h-2 bg-discord-green rounded-full mr-3"></div>
              <svg 
                class="w-5 h-5 text-discord-light-400 transition-all duration-200"
                :class="type === 'upload' ? 'rotate-180 text-discord-green' : ''"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </div>
          </button>
          
          <!-- Conteúdo do upload -->
          <div v-if="type === 'upload'" class="px-5 pb-5 bg-gradient-to-b from-discord-green/5 to-transparent">
            <div class="border-t border-discord-dark-200 pt-4">
              <!-- Preview da imagem atual (se houver) -->
              <div v-if="selectedImage" class="mb-4">
                <div class="flex items-center justify-between p-3 bg-discord-dark-200 rounded">
                  <div class="flex items-center">
                    <img :src="selectedImage" :alt="t('createWorkspace.visual.currentImageAlt')" class="w-12 h-12 object-cover rounded mr-3" />
                    <div>
                      <p class="text-sm font-medium text-discord-light-100">{{ t('createWorkspace.visual.currentImage') }}</p>
                      <p class="text-xs text-discord-light-500">{{ t('createWorkspace.visual.clickToRemove') }}</p>
                    </div>
                  </div>
                  <button
                    @click="removeImage"
                    type="button"
                    class="p-2 text-discord-light-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-all duration-200"
                    :title="t('createWorkspace.visual.removeImage')"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </div>
              </div>
              
              <!-- Upload de nova imagem -->
              <ImageUploadCrop @image-uploaded="handleImageUploaded" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import IconSelector from './IconSelector.vue'
import ImageUploadCrop from './ImageUploadCrop.vue'

const { t } = useI18n()

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      type: 'icon',
      icon: 'task',
      image: ''
    })
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const type = ref(props.modelValue.type || 'icon')
const selectedIcon = ref(props.modelValue.icon || 'task')
const selectedImage = ref(props.modelValue.image || '')

const setType = (newType) => {
  type.value = newType
  updateModelValue()
}

const handleImageUploaded = (imageUrl) => {
  console.log('🖼️ === IMAGEM ENVIADA ===')
  console.log('🖼️ URL recebida:', imageUrl)
  console.log('🖼️ Estado ANTES:', { type: type.value, image: selectedImage.value })
  
  // FORÇAR mudança para upload
  type.value = 'upload'
  selectedImage.value = imageUrl
  
  console.log('🖼️ Estado APÓS:', { type: type.value, image: selectedImage.value })
  
  // FORÇAR atualização imediata
  updateModelValue()
  
  console.log('🖼️ === IMAGEM CONFIGURADA ===')
  console.log('🖼️ selectedImage.value:', selectedImage.value)
  console.log('🖼️ type.value:', type.value)
}

const removeImage = () => {
  console.log('🗑️ === REMOVENDO IMAGEM ===')
  console.log('🗑️ Imagem atual:', selectedImage.value)
  
  // Limpar imagem e voltar para ícone
  selectedImage.value = ''
  type.value = 'icon'
  
  console.log('🗑️ Estado após remoção:', { type: type.value, image: selectedImage.value })
  
  // Atualizar valor emitido
  updateModelValue()
  
  console.log('🗑️ === IMAGEM REMOVIDA ===')
}

const updateModelValue = () => {
  const value = {
    type: type.value,
    icon: selectedIcon.value,
    image: selectedImage.value
  }
  console.log('📤 === EMITINDO PARA PARENT ===')
  console.log('📤 Valor emitido:', value)
  console.log('📤 type:', value.type)
  console.log('📤 image:', value.image)
  emit('update:modelValue', value)
}

// Flag para controlar se já foi inicializado
let isInitialized = false

// Watch para sincronizar com prop - APENAS na inicialização
watch(() => props.modelValue, (newValue) => {
  if (newValue && !isInitialized) {
    console.log('🔄 WorkspaceIconSelector - Inicializando com modelValue:', newValue)
    
    // Atualizar tipo
    type.value = newValue.type || 'icon'
    
    // Atualizar ícone
    selectedIcon.value = newValue.icon || 'task'
    
    // Atualizar imagem apenas se não temos uma
    if (!selectedImage.value || !selectedImage.value.trim()) {
      selectedImage.value = newValue.image || ''
    }
    
    isInitialized = true
    console.log('🔄 WorkspaceIconSelector - Inicializado:', { type: type.value, icon: selectedIcon.value, image: selectedImage.value })
  }
}, { immediate: true })

// Watch para mudanças internas
watch([type, selectedIcon, selectedImage], () => {
  updateModelValue()
})
</script>
